/* eslint-disable @typescript-eslint/no-explicit-any */
// semanticTokens.ts - Semantic color token definitions for light and dark themes

export type ThemeMode = "light" | "dark";

/**
 * Semantic token structure maps semantic names to palette color references
 * Format: { tokenName: "palette.shade" } -> e.g., { "background.neutral.default": "slate.700" }
 */
type SemanticTokenMap = Record<string, string>;

/**
 * Light mode semantic token mappings
 * Maps semantic token names to palette color references
 */
export const lightSemanticTokens: SemanticTokenMap = {
  // Background tokens
  "background.default.default": "gray.100",
  "background.default.secondary": "gray.300",
  "background.default.tertiary": "gray.500",
  "background.default.default-hover": "gray.200",
  "background.default.secondary-hover": "gray.400",
  "background.default.tertiary-hover": "gray.600",

  "background.wishlink.default": "wishlink.500",
  "background.wishlink.secondary": "wishlink.300",
  "background.wishlink.tertiary": "wishlink.100",
  "background.wishlink.hover": "wishlink.600",
  "background.wishlink.secondary-hover": "wishlink.400",
  "background.wishlink.tertiary-hover": "wishlink.200",

  "background.positive.default": "green.500",
  "background.positive.secondary": "green.200",
  "background.positive.tertiary": "green.100",
  "background.positive.hover": "green.600",
  "background.positive.secondary-hover": "green.300",
  "background.positive.tertiary-hover": "green.200",

  "background.warning.default": "yellow.500",
  "background.warning.secondary": "yellow.300",
  "background.warning.tertiary": "yellow.100",
  "background.warning.hover": "yellow.600",
  "background.warning.secondary-hover": "yellow.400",
  "background.warning.tertiary-hover": "yellow.200",

  "background.danger.default": "red.500",
  "background.danger.secondary": "red.300",
  "background.danger.tertiary": "red.100",
  "background.danger.hover": "red.600",
  "background.danger.secondary-hover": "red.400",
  "background.danger.tertiary-hover": "red.200",

  "background.disabled.default": "gray.400",

  "background.neutral.default": "slate.700",
  "background.neutral.hover": "slate.800",
  "background.neutral.secondary": "slate.300",
  "background.neutral.secondary-hover": "slate.400",
  "background.neutral.tertiary": "slate.200",
  "background.neutral.tertiary-hover": "slate.300",

  "background.utilities.scrim": "#ffffff",
  "background.utilities.overlay": "#000000",
  "background.utilities.blanket": "#000000",
  "background.utilities.measurement": "blue.100",

  // Text tokens
  "text.default.default": "gray.1000",
  "text.default.secondary": "gray.600",
  "text.default.tertiary": "gray.400",

  "text.wishlink.default": "wishlink.500",
  "text.wishlink.secondary": "wishlink.800",
  "text.wishlink.tertiary": "wishlink.200",
  "text.wishlink.on-wishlink": "white.1000",
  "text.wishlink.on-wishlink-secondary": "wishlink.900",
  "text.wishlink.on-wishlink-tertiary": "wishlink.500",

  "text.positive.default": "green.900",
  "text.positive.secondary": "green.700",
  "text.positive.tertiary": "green.500",
  "text.positive.on-positive": "green.100",
  "text.positive.on-positive-secondary": "green.700",
  "text.positive.on-positive-tertiary": "green.900",

  "text.warning.default": "yellow.900",
  "text.warning.secondary": "yellow.700",
  "text.warning.tertiary": "yellow.400",
  "text.warning.on-warning": "gray.800",
  "text.warning.on-warning-secondary": "yellow.800",
  "text.warning.on-warning-tertiary": "yellow.1000",

  "text.danger.default": "red.700",
  "text.danger.secondary": "red.600",
  "text.danger.tertiary": "red.500",
  "text.danger.on-danger": "red.100",
  "text.danger.on-danger-secondary": "red.700",
  "text.danger.on-danger-tertiary": "red.700",

  "text.disabled.default": "gray.300",
  "text.disabled.on-disabled": "gray.600",

  "text.neutral.default": "slate.900",
  "text.neutral.secondary": "slate.700",
  "text.neutral.tertiary": "slate.600",
  "text.neutral.on-neutral": "slate.100",
  "text.neutral.on-neutral-secondary": "slate.900",
  "text.neutral.on-neutral-tertiary": "slate.800",

  "text.utilities.text-on-overlay": "black.1000",
  "text.utilities.text-on-measurement": "blue.500",

  // Icon tokens
  "icon.default.default": "gray.1000",
  "icon.default.secondary": "gray.600",
  "icon.default.tertiary": "gray.400",

  "icon.wishlink.default": "wishlink.500",
  "icon.wishlink.secondary": "wishlink.400",
  "icon.wishlink.tertiary": "wishlink.300",
  "icon.wishlink.on-wishlink": "white.1000",
  "icon.wishlink.on-wishlink-secondary": "wishlink.900",
  "icon.wishlink.on-wishlink-tertiary": "wishlink.500",

  "icon.positive.default": "green.900",
  "icon.positive.secondary": "green.700",
  "icon.positive.tertiary": "green.500",
  "icon.positive.on-positive": "green.100",
  "icon.positive.on-positive-secondary": "green.700",
  "icon.positive.on-positive-tertiary": "green.900",

  "icon.warning.default": "yellow.900",
  "icon.warning.secondary": "yellow.700",
  "icon.warning.tertiary": "yellow.400",
  "icon.warning.on-warning": "gray.800",
  "icon.warning.on-warning-secondary": "yellow.800",
  "icon.warning.on-warning-tertiary": "yellow.900",

  "icon.danger.default": "red.700",
  "icon.danger.secondary": "red.600",
  "icon.danger.tertiary": "red.500",
  "icon.danger.on-danger": "red.100",
  "icon.danger.on-danger-secondary": "red.700",
  "icon.danger.on-danger-tertiary": "red.700",

  "icon.disabled.default": "gray.400",
  "icon.disabled.on-disabled": "gray.600",

  "icon.neutral.default": "slate.900",
  "icon.neutral.secondary": "slate.700",
  "icon.neutral.tertiary": "slate.600",
  "icon.neutral.on-neutral": "slate.100",
  "icon.neutral.on-neutral-secondary": "slate.900",
  "icon.neutral.on-neutral-tertiary": "slate.800",

  "icon.utilities.icon-on-measurement": "blue.500",
  "icon.utilities.icon": "blue.500",

  // Border tokens
  "border.default.default": "gray.400",
  "border.default.secondary": "gray.600",
  "border.default.tertiary": "gray.700",

  "border.wishlink.default": "wishlink.500",
  "border.wishlink.secondary": "wishlink.300",
  "border.wishlink.tertiary": "wishlink.100",

  "border.positive.default": "green.700",
  "border.positive.secondary": "green.400",
  "border.positive.tertiary": "green.200",

  "border.warning.default": "yellow.900",
  "border.warning.secondary": "yellow.700",
  "border.warning.tertiary": "yellow.500",

  "border.danger.default": "red.700",
  "border.danger.secondary": "red.500",
  "border.danger.tertiary": "red.300",

  "border.disabled.default": "gray.500",

  "border.neutral.default": "slate.900",
  "border.neutral.secondary": "slate.600",
  "border.neutral.tertiary": "slate.400",

  "border.utilities.measurement": "blue.500",
  "border.utilities.swatch": "#000000",

  // Legacy wishlink colors (wishlink-legacy mappings)
  "background.wishlink-legacy.default": "wishlink-legacy.600",
  "background.wishlink-legacy.hover": "wishlink-legacy.700",
  "background.wishlink-legacy.secondary": "wishlink-legacy.300",
  "background.wishlink-legacy.secondary-hover": "wishlink-legacy.400",
  "background.wishlink-legacy.tertiary": "wishlink-legacy.100",
  "background.wishlink-legacy.tertiary-hover": "wishlink-legacy.200",

  "background.utilities.measurement-alt": "blue.500",
  "text.utilities.text-on-measurement-alt": "blue.200",
  "icon.utilities.icon-on-measurement-alt": "blue.100",
};

/**
 * Dark mode semantic token mappings
 * Maps semantic token names to palette color references
 */
export const darkSemanticTokens: SemanticTokenMap = {
  // Background tokens
  "background.default.default": "gray.1000",
  "background.default.secondary": "gray.700",
  "background.default.tertiary": "gray.600",
  "background.default.default-pressed": "gray.700",
  "background.default.secondary-pressed": "gray.600",
  "background.default.tertiary-pressed": "gray.500",

  "background.wishlink.default": "wishlink.600",
  "background.wishlink.secondary": "wishlink.700",
  "background.wishlink.tertiary": "wishlink.900",
  "background.wishlink.hover": "wishlink.500",
  "background.wishlink.secondary-pressed": "wishlink.600",
  "background.wishlink.tertiary-pressed": "wishlink.800",

  "background.positive.default": "green.500",
  "background.positive.secondary": "green.700",
  "background.positive.tertiary": "green.900",
  "background.positive.hover": "green.400",
  "background.positive.secondary-pressed": "green.600",
  "background.positive.tertiary-pressed": "green.800",

  "background.warning.default": "yellow.500",
  "background.warning.secondary": "yellow.700",
  "background.warning.tertiary": "yellow.900",
  "background.warning.hover": "yellow.400",
  "background.warning.secondary-pressed": "yellow.600",
  "background.warning.tertiary-pressed": "yellow.800",

  "background.danger.default": "red.500",
  "background.danger.secondary": "red.700",
  "background.danger.tertiary": "red.1000",
  "background.danger.hover": "red.400",
  "background.danger.secondary-pressed": "red.800",
  "background.danger.tertiary-hover": "red.900",

  "background.disabled.default": "gray.700",

  "background.neutral.default": "slate.400",
  "background.neutral.hover": "slate.500",
  "background.neutral.secondary": "slate.900",
  "background.neutral.secondary-pressed": "slate.1000",
  "background.neutral.tertiary": "slate.900",
  "background.neutral.tertiary-pressed": "slate.1000",

  "background.utilities.scrim": "#000000",
  "background.utilities.overlay": "#000000",
  "background.utilities.blanket": "#000000",
  "background.utilities.info-primary": "blue.500",

  // Text tokens
  "text.default.default": "white.1000",
  "text.default.secondary": "white.500",
  "text.default.tertiary": "white.300",

  "text.wishlink.default": "wishlink.100",
  "text.wishlink.secondary": "wishlink.300",
  "text.wishlink.tertiary": "wishlink.900",
  "text.wishlink.on-wishlink": "white.1000",
  "text.wishlink.on-wishlink-secondary": "wishlink.900",
  "text.wishlink.on-wishlink-tertiary": "wishlink.500",

  "text.positive.default": "green.200",
  "text.positive.secondary": "green.400",
  "text.positive.tertiary": "green.600",
  "text.positive.on-positive": "green.100",
  "text.positive.on-positive-secondary": "green.400",
  "text.positive.on-positive-tertiary": "green.600",

  "text.warning.default": "yellow.200",
  "text.warning.secondary": "yellow.400",
  "text.warning.tertiary": "yellow.600",
  "text.warning.on-warning": "gray.800",
  "text.warning.on-warning-secondary": "yellow.200",
  "text.warning.on-warning-tertiary": "yellow.100",

  "text.danger.default": "red.200",
  "text.danger.secondary": "red.400",
  "text.danger.tertiary": "red.500",
  "text.danger.on-danger": "red.100",
  "text.danger.on-danger-secondary": "red.100",
  "text.danger.on-danger-tertiary": "red.100",

  "text.disabled.default": "gray.700",
  "text.disabled.on-disabled": "gray.600",

  "text.neutral.default": "slate.200",
  "text.neutral.secondary": "slate.300",
  "text.neutral.tertiary": "slate.400",
  "text.neutral.on-neutral": "slate.100",
  "text.neutral.on-neutral-secondary": "slate.100",
  "text.neutral.on-neutral-tertiary": "slate.200",

  "text.utilities.text-on-overlay": "white.1000",
  "text.utilities.text-on-info-primary": "blue.100",

  // Icon tokens
  "icon.default.default": "white.1000",
  "icon.default.secondary": "white.500",
  "icon.default.tertiary": "white.300",

  "icon.wishlink.default": "wishlink.100",
  "icon.wishlink.secondary": "wishlink.300",
  "icon.wishlink.tertiary": "wishlink.400",
  "icon.wishlink.on-wishlink": "white.1000",
  "icon.wishlink.on-wishlink-secondary": "wishlink.900",
  "icon.wishlink.on-wishlink-tertiary": "wishlink.500",

  "icon.positive.default": "green.200",
  "icon.positive.secondary": "green.400",
  "icon.positive.tertiary": "green.600",
  "icon.positive.on-positive": "green.100",
  "icon.positive.on-positive-secondary": "green.400",
  "icon.positive.on-positive-tertiary": "green.600",

  "icon.warning.default": "yellow.200",
  "icon.warning.secondary": "yellow.400",
  "icon.warning.tertiary": "yellow.600",
  "icon.warning.on-warning": "gray.800",
  "icon.warning.on-warning-secondary": "yellow.200",
  "icon.warning.on-warning-tertiary": "yellow.100",

  "icon.danger.default": "red.200",
  "icon.danger.secondary": "red.400",
  "icon.danger.tertiary": "red.500",
  "icon.danger.on-danger": "red.100",
  "icon.danger.on-danger-secondary": "red.100",
  "icon.danger.on-danger-tertiary": "red.100",

  "icon.disabled.default": "gray.700",
  "icon.disabled.on-disabled": "gray.600",

  "icon.neutral.default": "slate.200",
  "icon.neutral.secondary": "slate.300",
  "icon.neutral.tertiary": "slate.400",
  "icon.neutral.on-neutral": "slate.100",
  "icon.neutral.on-neutral-secondary": "slate.100",
  "icon.neutral.on-neutral-tertiary": "slate.100",

  "icon.utilities.icon-on-info-primary": "blue.100",
  "icon.utilities.info": "blue.100",

  // Border tokens
  "border.default.default": "gray.700",
  "border.default.secondary": "gray.600",
  "border.default.tertiary": "gray.500",

  "border.wishlink.default": "wishlink.100",
  "border.wishlink.secondary": "wishlink.300",
  "border.wishlink.tertiary": "wishlink.400",

  "border.positive.default": "green.200",
  "border.positive.secondary": "green.400",
  "border.positive.tertiary": "green.600",

  "border.warning.default": "yellow.200",
  "border.warning.secondary": "yellow.400",
  "border.warning.tertiary": "yellow.600",

  "border.danger.default": "red.200",
  "border.danger.secondary": "red.400",
  "border.danger.tertiary": "red.600",

  "border.disabled.default": "gray.700",

  "border.neutral.default": "slate.100",
  "border.neutral.secondary": "slate.500",
  "border.neutral.tertiary": "slate.600",

  "border.utilities.info": "blue.100",
  "border.utilities.swatch": "#ffffff",

  // Legacy wishlink colors
  "background.wishlink-legacy.default": "wishlink-legacy.600",
  "background.wishlink-legacy.hover": "wishlink-legacy.500",
  "background.wishlink-legacy.secondary": "wishlink-legacy.300",
  "background.wishlink-legacy.secondary-pressed": "wishlink-legacy.200",
  "background.wishlink-legacy.tertiary": "wishlink-legacy.900",
  "background.wishlink-legacy.tertiary-pressed": "wishlink-legacy.800",

  "background.utilities.info-secondary": "blue.100",
  "text.utilities.text-on-info-secondary": "blue.500",
  "icon.utilities.icon-on-info-secondary": "blue.500",
};

/**
 * Get semantic tokens for a specific theme
 */
export const getSemanticTokens = (theme: ThemeMode): SemanticTokenMap => {
  return theme === "light" ? lightSemanticTokens : darkSemanticTokens;
};

/**
 * Type definition for all semantic token keys
 * This enables TypeScript autocomplete for semantic token names
 */
export type SemanticColorToken = keyof typeof lightSemanticTokens;
